import { parse } from 'node-html-parser'
import { useContext } from 'react'
import { useTranslation } from 'next-i18next'
import Head from 'next/head'
import PropTypes from 'prop-types'

// Context
import { AppContext } from '../../context/AppContext'

// Utils
import createMarkup from '../../utils/dom/createMarkup'
import { UserContext } from '../../context/UserContext'

const Seo = (props) => {
  // Context
  const appContext = useContext(AppContext)
  const userContext = useContext(UserContext)

  // Translations
  const { t } = useTranslation(
    userContext?.userSession?.sportType?.slug ??
      process.env.NEXT_PUBLIC_RINUS_DEFAULT_NAMESPACE ??
      process.env.NEXT_PUBLIC_RINUS_DEFAULT_SPORT
  )

  const theme = process.env.NEXT_PUBLIC_THEME
  const themeColor = {
    knvb: '#00A9DB',
    frf: '#0B3575',
    fmf: '#0B3575'
  }
  const metaTitleContainer = JSON.parse(props.metaTitleContainer).title || { title: t('SEO__FALLBACK_TITLE') }
  const metaDescriptionContainer = JSON.parse(props.metaTagContainer).description || {
    content: t('SEO__FALLBACK_DESCRIPTION')
  }
  const metaScriptContainer = JSON.parse(props.metaScriptContainer)
  const metaLinkContainer = JSON.parse(props.metaLinkContainer)
  const metaTagContainer = JSON.parse(props.metaTagContainer)
  const metaJsonLdContainer = JSON.parse(props.metaJsonLdContainer)

  let gTagElement, gTagScript
  if (metaScriptContainer.gtag && metaScriptContainer.gtag.bodyScript) {
    gTagElement = parse(metaScriptContainer.gtag.bodyScript)
    gTagScript = (
      <script
        async
        type="text/plain"
        data-cmp-src={gTagElement.querySelector('script').getAttribute('src')}
        className="cmplazyload"
        data-cmp-vendor="s905"
        key="gtag-js"
      />
    )
  }

  let googleTagManagerElement, googleTagManagerScript
  if (metaScriptContainer.googleTagManager && metaScriptContainer.googleTagManager.bodyScript) {
    googleTagManagerElement = parse(metaScriptContainer.googleTagManager.bodyScript)
    googleTagManagerScript = (
      <noscript
        key="google-tag-manager"
        dangerouslySetInnerHTML={createMarkup(googleTagManagerElement.firstChild.firstChild.rawText)}
      />
    )
  }

  let facebookPixelElement, facebookPixelScript
  if (metaScriptContainer.facebookPixel && metaScriptContainer.facebookPixel.bodyScript) {
    facebookPixelElement = parse(metaScriptContainer.facebookPixel.bodyScript)
    facebookPixelScript = (
      <noscript
        key="facebook-pixel"
        dangerouslySetInnerHTML={createMarkup(facebookPixelElement.firstChild.firstChild.rawText)}
      />
    )
  }

  let linkedInInsightElement, linkedInInsightScript
  if (metaScriptContainer.linkedInInsight && metaScriptContainer.linkedInInsight.bodyScript) {
    linkedInInsightElement = parse(metaScriptContainer.linkedInInsight.bodyScript)
    linkedInInsightScript = (
      <script
        type="text/plain"
        className="cmplazyload"
        dangerouslySetInnerHTML={createMarkup(linkedInInsightElement.firstChild.innerText)}
        key="linkedin-insight"
        data-cmp-vendor="s2"
      />
    )
  }

  // Create meta elements
  const MetaElements = Object.keys(metaTagContainer).map((key) => {
    if (metaTagContainer[key].length === 0) return false
    return <meta key={key} property={key} content={metaTagContainer[key].content} />
  })

  // Create meta links
  const MetaLinkElements = Object.keys(metaLinkContainer).map((key) => {
    // @todo find a solution for multiple link properties like alternate
    if (metaLinkContainer[key].length === 0 || metaLinkContainer[key].length > 1) return false
    return (
      <link
        key={key}
        type={metaLinkContainer[key].type}
        rel={metaLinkContainer[key].rel}
        href={metaLinkContainer[key].href}
      />
    )
  })

  // Create json ld
  const MetaJsonLdElements = Object.keys(metaJsonLdContainer).map((key) => {
    return (
      <script
        key={key}
        type="application/ld+json"
        dangerouslySetInnerHTML={createMarkup(JSON.stringify(metaJsonLdContainer[key]))}
      />
    )
  })

  return (
    <>
      <Head>
        <title key="title">{metaTitleContainer.title}</title>
        <meta name="description" content={metaDescriptionContainer.content} />

        {/* Add meta elements */}
        {MetaElements}

        {/* Add link elements */}
        {MetaLinkElements}

        {/* Favicon manifest */}
        <title key={'title'}>{metaTitleContainer.title}</title>
        <link rel="apple-touch-icon" sizes="180x180" href={`/favicons/${theme}/apple-touch-icon.png?v=3`} />
        <link rel="icon" type="image/png" sizes="32x32" href={`/favicons/${theme}/favicon-32x32.png?v=3`} />
        <link rel="icon" type="image/png" sizes="16x16" href={`/favicons/${theme}/favicon-16x16.png?v=3`} />
        <link rel="manifest" href={`/favicons/${theme}/site.webmanifest?v=3`} />
        <link rel="mask-icon" href={`/favicons/${theme}/safari-pinned-tab.svg?v=3`} color={themeColor[theme]} />
        <link rel="shortcut icon" href={`/favicons/${theme}/favicon.ico?v=3`} />
        <meta name="apple-mobile-web-app-title" content={t('SEO__APP_NAME')} />
        <meta name="application-name" content={t('SEO__APP_NAME')} />
        <meta name="msapplication-TileColor" content={themeColor[theme]} />
        <meta name="msapplication-config" content={`/favicons/${theme}/browserconfig.xml?v=3`} />
        <meta name="theme-color" content={themeColor[theme]} />

        {theme === 'frf' && (
          <meta name="google-site-verification" content="kmzwBDwvByP7Xq_qnJIhu9MJB6bcfPj8D1FkyRL1C00" />
        )}

        {theme === 'sportwide' && (
          <meta
            property="og:image"
            content={`${process.env.NEXT_PUBLIC_URL}/images/static/${theme}/og-share-image.jpg`}
          />
        )}

        {/* Add Json LD scripts */}
        {MetaJsonLdElements}

        {/* Tracking scripts */}
        {gTagScript}

        {appContext?.contextMounted &&
          Object.keys(metaScriptContainer).map((key) => {
            if (
              !metaScriptContainer[key].script ||
              (key !== 'googleAnalytics' && appContext?.isCookieConsentRejected === 'yes')
            )
              return false
            return <script key={key} dangerouslySetInnerHTML={createMarkup(metaScriptContainer[key].script)} />
          })}
      </Head>

      {/* Implement tracking scripts in `body` */}
      {appContext?.isCookieConsentRejected === 'no' && (
        <>
          {googleTagManagerScript}
          {facebookPixelScript}
          {linkedInInsightScript}
        </>
      )}
    </>
  )
}

/* eslint-enable no-unused-vars */

export default Seo

Seo.propTypes = {
  metaTitleContainer: PropTypes.string,
  metaScriptContainer: PropTypes.string,
  metaLinkContainer: PropTypes.string,
  metaTagContainer: PropTypes.string,
  metaJsonLdContainer: PropTypes.string
}
